import { useCallback, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  CheckCircle,
  Loader2,
  FileText,
  ArrowLeft,
  Briefcase,
  Shield,
  X,
  Mail,
} from "lucide-react"
import { toast } from "sonner"
import type { AxiosError } from "axios"
import type { ApiResult } from "@/lib/api-client"
import { FileCategory } from "@/modules/shared/hooks/use-file-validation"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { Separator } from "@/modules/shared/ui/components/separator"
import { Badge } from "@/modules/shared/ui/components/badge"
import { DraggableFileUpload } from "@/modules/shared/ui/components/custom/file-upload"
import { MultiInput } from "@/modules/shared/ui/components/custom/MultiInput"
import {
  getVacancyBrief,
  extractAndStageCv,
  submitPublicApplication,
  fetchApplicantTerms,
  recordApplicantConsent,
  type PublicCvExtractResponse,
  type VacancyBrief,
  type ApplicantTermsDoc,
} from "@/modules/public/infrastructure/public-apply-service"

type Step = "upload" | "review" | "done"

const reviewSchema = z.object({
  firstName: z.string().min(1, "First name is required."),
  lastName: z.string().min(1, "Last name is required."),
  email: z.email({ message: "Please enter a valid email address." }),
  phone: z.string().optional(),
  location: z.string().optional(),
  education: z.string().optional(),
  idNumber: z.string().max(13).optional(),
})

type ReviewFormValues = z.infer<typeof reviewSchema>

interface ApplicantTosModalProps {
  doc: ApplicantTermsDoc
  onAccept: () => Promise<void>
  onClose: () => void
  isSubmitting: boolean
}

function ApplicantTosModal({ doc, onAccept, onClose, isSubmitting }: ApplicantTosModalProps) {
  const [checked, setChecked] = useState(false)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
    >
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#1b237a]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1b237a] leading-tight">
                Terms &amp; Conditions
              </h2>
              <p className="text-xs text-muted-foreground">
                Version {doc.version} · Please read before continuing
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-full p-1 hover:bg-muted"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          <div
            className="prose prose-sm max-w-none prose-headings:text-[#1b237a] prose-headings:font-bold prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-a:text-[#40C4FF]"
            dangerouslySetInnerHTML={{ __html: doc.content }}
          />
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted shrink-0 space-y-3">
          <div className="flex items-start gap-3">
            <input
              id="tos-checkbox"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="w-4 h-4 mt-0.5 cursor-pointer accent-[#1b237a] shrink-0"
            />
            <label htmlFor="tos-checkbox" className="text-sm text-foreground cursor-pointer leading-snug">
              I have read and agree to the{" "}
              <a
                href="/legal/applicant-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#40C4FF] hover:underline font-medium whitespace-nowrap"
              >
                Terms &amp; Conditions
              </a>
            </label>
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-none"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!checked || isSubmitting}
              onClick={onAccept}
              className="flex-1 bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] hover:from-[#1a2a8f] hover:to-[#1b237a] text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording consent...
                </>
              ) : (
                "Accept & Continue"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ApplyPage() {
  const { applyToken } = useParams<{ applyToken: string }>()

  const [vacancyId, orgId] = (() => {
    try {
      const padded = (applyToken ?? "").replace(/-/g, "+").replace(/_/g, "/")
      const decoded = atob(padded)
      return decoded.split("|")
    } catch {
      return ["", ""]
    }
  })()

  const applicationEmail = import.meta.env.VITE_RECRUITMENT_EMAIL_RECIPIENT as
    | string
    | undefined
  const mailtoHref = (() => {
    if (!applicationEmail || !vacancyId) return ""
    const termsUrl = `${window.location.origin}/legal/applicant-terms`
    const subject = `[Vacancy Ref: ${vacancyId}]`
    const body = [
      "Dear Hiring Team,",
      "",
      "Please find my CV attached in application for the above-referenced position.",
      "",
      "---",
      `By submitting this application I confirm that I have read and agreed to the Terms & Conditions: ${termsUrl}`,
    ].join("\n")
    return `mailto:${applicationEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  })()

  const recaptchaScriptReady = useRef(false)

  useEffect(() => {
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string
    if (!siteKey) return
    const existing = document.querySelector(`script[src*="enterprise.js"]`)
    if (existing) {
      recaptchaScriptReady.current = true
      return
    }
    const script = document.createElement("script")
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
    script.async = true
    script.onload = () => {
      recaptchaScriptReady.current = true
    }
    document.head.appendChild(script)
    return () => {
      if (document.head.contains(script)) document.head.removeChild(script)
      recaptchaScriptReady.current = false
    }
  }, [])

  const getRecaptchaToken = useCallback(
    async (action: string): Promise<string> => {
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string
      if (!siteKey) return ""
      const waitForReady = (): Promise<void> =>
        new Promise((resolve) => {
          const check = () => {
            if (
              recaptchaScriptReady.current &&
              typeof window.grecaptcha !== "undefined"
            ) {
              resolve()
            } else {
              setTimeout(check, 100)
            }
          }
          check()
        })
      await waitForReady()
      return new Promise((resolve) => {
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise
            .execute(siteKey, { action })
            .then(resolve)
        })
      })
    },
    [],
  )

  const [step, setStep] = useState<Step>("upload")
  const [isLoading, setIsLoading] = useState(false)
  const [vacancy, setVacancy] = useState<VacancyBrief | null>(null)
  const [skills, setSkills] = useState<string[]>([])
  const [certifications, setCertifications] = useState<string[]>([])
  const [experienceYears, setExperienceYears] = useState(0)
  const [cvMeta, setCvMeta] = useState<Pick<
    PublicCvExtractResponse,
    "blobUrl" | "fileName" | "contentType" | "fileSize"
  > | null>(null)

  // ── Consent state ──────────────────────────────────────────────────────────
  const [applicantTerms, setApplicantTerms] = useState<ApplicantTermsDoc | null>(null)
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [hasConsented, setHasConsented] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [isRecordingConsent, setIsRecordingConsent] = useState(false)
  // Incremented on modal cancel to force-remount DraggableFileUpload, resetting
  // its internal <input> so the same file can be re-selected after dismissal.
  const [uploadKey, setUploadKey] = useState(0)
  // ──────────────────────────────────────────────────────────────────────────

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      location: "",
      education: "",
      idNumber: "",
    },
  })

  useEffect(() => {
    if (!vacancyId || !orgId) return
    getVacancyBrief(orgId, vacancyId)
      .then((v) => setVacancy(v))
      .catch(() => { })
  }, [vacancyId, orgId])

  // Fetch applicant terms on mount
  useEffect(() => {
    fetchApplicantTerms()
      .then((doc) => setApplicantTerms(doc))
      .catch(() => {
        // Non-fatal — if terms can't load, skip consent gate gracefully
        console.warn("Could not load applicant terms document.")
      })
  }, [])

  // Actual upload logic, called after consent is confirmed
  const doUpload = async (file: File) => {
    if (!vacancyId || !orgId) return
    setIsLoading(true)
    try {
      const captchaToken = await getRecaptchaToken("cv_extract")
      const extracted = await extractAndStageCv(
        vacancyId,
        orgId,
        file,
        captchaToken,
      )
      setCvMeta({
        blobUrl: extracted.blobUrl,
        fileName: extracted.fileName,
        contentType: extracted.contentType,
        fileSize: extracted.fileSize,
      })
      reset({
        firstName: extracted.firstName ?? "",
        lastName: extracted.lastName ?? "",
        email: extracted.email ?? "",
        phone: extracted.phone ?? "",
        location: extracted.location ?? "",
        education: extracted.education ?? "",
        idNumber: extracted.idNumber ?? "",
      })
      setSkills(extracted.skills ?? [])
      setCertifications(extracted.certifications ?? [])
      setExperienceYears(extracted.experienceYears ?? 0)
      setStep("review")
    } catch (err) {
      const axiosErr = err as AxiosError<ApiResult<unknown>>
      toast.error(
        axiosErr.response?.data?.error ??
        "Could not read your CV. Please try again.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelected = async (file: File) => {
    // If terms couldn't load or consent already given, proceed directly
    if (!applicantTerms || hasConsented) {
      await doUpload(file)
      return
    }
    // Otherwise stash the file and show the consent modal
    setPendingFile(file)
    setShowConsentModal(true)
  }

  const handleConsentAccept = async () => {
    if (!applicantTerms) return
    setIsRecordingConsent(true)
    try {
      await recordApplicantConsent({ termsVersion: applicantTerms.version })
      setHasConsented(true)
      setShowConsentModal(false)
      if (pendingFile) {
        const fileToUpload = pendingFile
        setPendingFile(null)
        await doUpload(fileToUpload)
      }
    } catch {
      toast.error("Could not record your consent. Please try again.")
    } finally {
      setIsRecordingConsent(false)
    }
  }

  const handleConsentClose = () => {
    setShowConsentModal(false)
    setPendingFile(null)
    setUploadKey((k) => k + 1) // reset the file input so same file can be re-selected
  }

  const handleSubmitForm = async (values: ReviewFormValues) => {
    if (!cvMeta?.blobUrl || !vacancyId || !orgId) return
    setIsLoading(true)
    try {
      const captchaToken = await getRecaptchaToken("apply")
      await submitPublicApplication({
        blobUrl: cvMeta.blobUrl,
        fileName: cvMeta.fileName,
        contentType: cvMeta.contentType,
        fileSize: cvMeta.fileSize,
        vacancyId,
        organizationId: orgId,
        captchaToken,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone ?? null,
        location: values.location ?? null,
        skills,
        certifications,
        experienceYears,
        education: values.education ?? null,
        idNumber: values.idNumber ?? null,
      })
      setStep("done")
    } catch (err) {
      const axiosErr = err as AxiosError<ApiResult<unknown>>
      toast.error(
        axiosErr.response?.data?.error ??
        "Failed to submit your application. Please try again.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  const resetToUpload = () => {
    setStep("upload")
    setCvMeta(null)
    reset()
    setSkills([])
    setCertifications([])
    setExperienceYears(0)
  }

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Consent Modal */}
      {showConsentModal && applicantTerms && (
        <ApplicantTosModal
          doc={applicantTerms}
          onAccept={handleConsentAccept}
          onClose={handleConsentClose}
          isSubmitting={isRecordingConsent}
        />
      )}

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* ── STEP 1: Upload ── */}
        {step === "upload" && (
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] bg-clip-text text-transparent">
                Apply Now
              </CardTitle>
              <p className="text-muted-foreground text-sm mt-1">
                Upload your CV and we'll fill in your details automatically
              </p>
              {vacancy && (
                <div className="flex items-center justify-center gap-2 mt-3 px-4 py-2.5 bg-blue-50 rounded-lg border border-blue-100">
                  <Briefcase className="w-4 h-4 text-blue-600 shrink-0" />
                  <p className="text-sm text-blue-800">
                    Applying for{" "}
                    <span className="font-semibold">{vacancy.title}</span>
                    {vacancy.organizationName && (
                      <>
                        {" "}
                        at{" "}
                        <span className="font-semibold">
                          {vacancy.organizationName}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-4">
              {isLoading ? (
                <div className="flex flex-col items-center gap-3 py-12">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                  <p className="text-muted-foreground font-medium">
                    Reading your CV...
                  </p>
                  <p className="text-muted-foreground text-sm">
                    This usually takes a few seconds
                  </p>
                </div>
              ) : (
                <>
                  <DraggableFileUpload
                    key={uploadKey}
                    fileCategory={FileCategory.Document}
                    onFileSelected={handleFileSelected}
                  />
                  {applicantTerms && (
                    <p className="text-center text-xs text-muted-foreground mt-3">
                      By uploading your CV you will be asked to accept our{" "}
                      <a
                        href="/legal/applicant-terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#40C4FF] hover:underline"
                      >
                        Terms &amp; Conditions
                      </a>
                    </p>
                  )}

                  {mailtoHref && (
                    <>
                      <div className="flex items-center gap-3 my-6">
                        <Separator className="flex-1" />
                        <span className="text-xs text-muted-foreground uppercase tracking-wider">
                          or
                        </span>
                        <Separator className="flex-1" />
                      </div>
                      <Button
                        asChild
                        variant="outline"
                        className="w-full border-[#1b237a] text-[#1b237a] hover:bg-[#1b237a] hover:text-white"
                      >
                        <a href={mailtoHref}>
                          <Mail className="w-4 h-4 mr-2" />
                          Apply by Email
                        </a>
                      </Button>
                      <p className="text-center text-xs text-muted-foreground mt-2">
                        Opens your email client with everything pre-filled —
                        just attach your CV and send
                      </p>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── STEP 2: Review form ── */}
        {step === "review" && (
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <Card className="shadow-lg">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <Badge
                    variant="secondary"
                    className="bg-blue-50 text-blue-700"
                  >
                    Step 2 of 2
                  </Badge>
                </div>
                <CardTitle className="text-2xl font-bold text-[#1b237a]">
                  Review Your Details
                </CardTitle>
                <p className="text-muted-foreground text-sm">
                  We've pulled these from your CV. Fix anything that looks wrong
                  before submitting.
                </p>
                {vacancy && (
                  <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                    <Briefcase className="w-4 h-4 text-blue-600 shrink-0" />
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">{vacancy.title}</span>
                      {vacancy.organizationName && (
                        <> · {vacancy.organizationName}</>
                      )}
                    </p>
                  </div>
                )}
              </CardHeader>

              <CardContent className="space-y-6 pt-2">
                {/* Personal info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="firstName"
                      className="text-foreground font-medium"
                    >
                      First Name *
                    </Label>
                    <Input id="firstName" {...register("firstName")} />
                    {errors.firstName && (
                      <p className="text-sm font-medium text-red-500">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="lastName"
                      className="text-foreground font-medium"
                    >
                      Last Name *
                    </Label>
                    <Input id="lastName" {...register("lastName")} />
                    {errors.lastName && (
                      <p className="text-sm font-medium text-red-500">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-foreground font-medium">
                    Email *
                  </Label>
                  <Input id="email" type="email" {...register("email")} />
                  {errors.email && (
                    <p className="text-sm font-medium text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="phone"
                      className="text-foreground font-medium"
                    >
                      Phone
                    </Label>
                    <Input id="phone" {...register("phone")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="location"
                      className="text-foreground font-medium"
                    >
                      Location
                    </Label>
                    <Input id="location" {...register("location")} />
                  </div>
                </div>

                <Separator />

                {/* Professional info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="experienceYears"
                      className="text-foreground font-medium"
                    >
                      Years of Experience
                    </Label>
                    <Input
                      id="experienceYears"
                      type="number"
                      min={0}
                      value={experienceYears}
                      onChange={(e) =>
                        setExperienceYears(Number(e.target.value))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="education"
                      className="text-foreground font-medium"
                    >
                      Education
                    </Label>
                    <Input id="education" {...register("education")} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground font-medium">Skills</Label>
                  <MultiInput
                    values={skills}
                    onChange={setSkills}
                    placeholder="Add a skill and press Enter"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground font-medium">
                    Certifications
                  </Label>
                  <MultiInput
                    values={certifications}
                    onChange={setCertifications}
                    placeholder="Add a certification and press Enter"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="idNumber"
                    className="text-foreground font-medium"
                  >
                    SA ID Number
                  </Label>
                  <Input
                    id="idNumber"
                    {...register("idNumber")}
                    maxLength={13}
                    placeholder="13-digit ID number"
                  />
                  {errors.idNumber && (
                    <p className="text-sm font-medium text-red-500">
                      {errors.idNumber.message}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetToUpload}
                    disabled={isLoading}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Re-upload CV
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] hover:from-[#1a2a8f] hover:to-[#1b237a] text-white"
                  >
                    {isLoading && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    {isLoading ? "Submitting..." : "Submit Application"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        )}

        {/* ── STEP 3: Thank you ── */}
        {step === "done" && (
          <Card className="shadow-lg">
            <CardContent className="flex flex-col items-center text-center py-16 gap-4">
              <div className="rounded-full bg-green-100 p-5">
                <CheckCircle className="w-14 h-14 text-green-500" />
              </div>
              <CardTitle className="text-3xl font-bold text-[#1b237a]">
                Application Submitted!
              </CardTitle>
              <p className="text-muted-foreground max-w-sm">
                Thank you for applying. We'll be in touch if you're shortlisted.
              </p>
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <FileText className="w-4 h-4" />
                Your CV has been saved with your application
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  )
}
